/**
 * Web Adapter - 替换 Electron API 为 Web API
 * 这个文件提供与 preload.ts 相同的接口,但使用 Web API 实现
 */

import { version } from "../package.json";
import axios, { AxiosRequestConfig } from "axios";

// 环境变量配置
// 在生产环境中，如果 VITE_API_URL 是根域名，则添加 /api 前缀
const rawApiBaseUrl = (import.meta as any).env?.VITE_API_URL || "http://localhost:8000";

// 如果是生产环境域名，确保不重复添加 /api 前缀
// 因为代码中的接口请求已经包含了 /api 前缀，如 apiClient.get("/api/...")
const API_BASE_URL = rawApiBaseUrl;

const rawWsBaseUrl = (import.meta as any).env?.VITE_WS_URL || "ws://localhost:8000";

// 在生产环境中，如果 VITE_WS_URL 是根域名，则添加 /cable 前缀用于WebSocket连接
const WS_URL = 
  (import.meta as any).env?.PROD && 
  (rawWsBaseUrl.startsWith('wss://') || rawWsBaseUrl.startsWith('ws://')) && 
  !rawWsBaseUrl.includes('/cable') 
    ? rawWsBaseUrl.endsWith('/') 
      ? rawWsBaseUrl + 'cable' 
      : rawWsBaseUrl + '/cable'
    : rawWsBaseUrl;

// Web Storage 适配器
class WebStorage {
  private prefix = "enjoy_app_";

  get(key: string): any {
    const value = localStorage.getItem(this.prefix + key);
    try {
      return value ? JSON.parse(value) : null;
    } catch {
      return value;
    }
  }

  set(key: string, value: any): void {
    localStorage.setItem(this.prefix + key, JSON.stringify(value));
  }

  remove(key: string): void {
    localStorage.removeItem(this.prefix + key);
  }

  clear(): void {
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith(this.prefix)) {
        localStorage.removeItem(key);
      }
    });
  }
}

const storage = new WebStorage();

// API 客户端
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

// 添加请求拦截器
apiClient.interceptors.request.use((config: AxiosRequestConfig) => {
  const user = storage.get("user");
  if (user?.accessToken) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${user.accessToken}`;
  }
  // ✅ 添加游客 User-Id
  const currentUser = storage.get("currentUser");
  if (currentUser?.id) {
    config.headers = config.headers || {};
    config.headers['X-User-Id'] = currentUser.id;
  }
  return config;
});

// 事件发射器 (模拟 IPC)
class EventEmitter {
  private listeners: Map<string, Set<Function>> = new Map();

  on(event: string, callback: Function) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  off(event: string, callback?: Function) {
    if (callback) {
      this.listeners.get(event)?.delete(callback);
    } else {
      this.listeners.delete(event);
    }
  }

  emit(event: string, ...args: any[]) {
    this.listeners.get(event)?.forEach((callback) => callback(...args));
  }

  removeAllListeners(event?: string) {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }
}

const eventBus = new EventEmitter();

const isLocalFilesystemPath = (input: string) => {
  if (!input) return false;
  return (
    input.startsWith("file://") ||
    input.startsWith("/") ||
    /^[A-Za-z]:[\\/]/.test(input)
  );
};

const normalizeWebPathToUrl = (input: string) => {
  if (!input) return input;
  if (input.startsWith("file://")) return input;
  if (input.startsWith("http://") || input.startsWith("https://")) return input;
  if (input.startsWith("/")) return `${window.location.origin}${input}`;
  return input;
};

// 触发数据库更新事件
const dispatchDbUpdate = (model: string, action: string, record: any) => {
  const event = new CustomEvent("db-on-transaction", {
    detail: { model, action, record }
  });
  document.dispatchEvent(event);
};

// Web Enjoy App API 实现
export const createWebEnjoyApp = (): EnjoyAppType => ({
  app: {
    getPlatformInfo: async () => ({
      platform: navigator.platform,
      arch: navigator.userAgent.includes("x64") ? "x64" : "arm64",
      osVersion: navigator.userAgent,
      appVersion: version,
      version: version,
    }),
    reset: async () => {
      localStorage.clear();
      sessionStorage.clear();
      if (navigator.storage && navigator.storage.persisted) {
        await navigator.storage.persist();
      }
      window.location.reload();
    },
    resetSettings: async () => {
      storage.clear();
    },
    relaunch: async () => {
      window.location.reload();
    },
    reload: async () => {
      window.location.reload();
    },
    isPackaged: async () => (import.meta as any).env?.PROD || false,
    apiUrl: async () => API_BASE_URL,
    wsUrl: async () => WS_URL,
    quit: async () => {
      window.close();
    },
    checkForUpdates: async () => {
      console.warn("checkForUpdates: 不支持 Web 版本");
    },
    quitAndInstall: async () => {
      console.warn("quitAndInstall: 不支持 Web 版本");
    },
    onUpdater: () => {},
    removeUpdaterListeners: () => {},
    openDevTools: async () => {
      console.log("请使用浏览器开发者工具");
    },
    createIssue: async (title: string, body: string) => {
      const res = await apiClient.post("/api/issues", { title, body });
      return res.data;
    },
    onCmdOutput: () => {},
    removeCmdOutputListeners: () => {},
    diskUsage: async () => {
      // Web 版本返回空数组
      return [];
    },
    version,
  },

  window: {
    fullscreen: async () => {
      await document.documentElement.requestFullscreen();
    },
    unfullscreen: async () => {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    },
    isMaximized: async () => 
      window.outerWidth === screen.width && window.outerHeight === screen.height,
    toggleMaximized: async () => {
      console.warn("toggleMaximized: 不支持 Web 版本");
    },
    maximize: async () => {},
    unmaximize: async () => {},
    minimize: async () => {},
    close: async () => {
      window.close();
    },
    onChange: (callback: any) => {
      eventBus.on("window-on-change", callback);
    },
    removeListener: (listener: any) => {
      eventBus.off("window-on-change", listener);
    },
    removeAllListeners: () => {
      eventBus.removeAllListeners("window-on-change");
    },
  },

  system: {
    preferences: {
      mediaAccess: async (mediaType: "microphone") => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: mediaType === "microphone",
          });
          stream.getTracks().forEach((track) => track.stop());
          return true;
        } catch {
          return false;
        }
      },
    },
    proxy: {
      get: async () => storage.get("proxy_config") || {},
      set: async (config: ProxyConfigType) => {
        storage.set("proxy_config", config);
      },
      refresh: async () => {},
    },
  },

  providers: {
    audible: {
      bestsellers: async (params: any) => {
        const res = await apiClient.get("/api/providers/audible/bestsellers", { params });
        return res.data;
      },
    },
    ted: {
      talks: async () => {
        const res = await apiClient.get("/api/providers/ted/talks");
        return res.data;
      },
      ideas: async () => {
        const res = await apiClient.get("/api/providers/ted/ideas");
        return res.data;
      },
      downloadTalk: async (url: string) => {
        const res = await apiClient.post("/api/providers/ted/download", { url });
        return res.data;
      },
    },
    youtube: {
      videos: async (channel: string) => {
        const res = await apiClient.get(`/api/providers/youtube/videos/${channel}`);
        return res.data;
      },
    },
  },

  view: {
    load: async () => {
      console.warn("view.load: 不支持 Web 版本");
    },
    hide: async () => {},
    remove: async () => {},
    show: async () => {},
    scrape: async (url: string) => {
      const res = await apiClient.post("/api/scrape", { url });
      return res.data;
    },
    resize: async () => {},
    loadCommunity: async () => {},
    onViewState: () => {},
    removeViewStateListeners: () => {},
  },

  onNotification: (callback: any) => {
    eventBus.on("on-notification", callback);
  },

  lookup: (selection: string, context: string, position: { x: number; y: number }) => {
    eventBus.emit("on-lookup", null, selection, context, position);
  },

  onLookup: (callback: any) => {
    eventBus.on("on-lookup", callback);
  },

  offLookup: () => {
    eventBus.removeAllListeners("on-lookup");
  },

  onTranslate: (callback: any) => {
    eventBus.on("on-translate", callback);
  },

  shell: {
    openExternal: async (url: string) => {
      window.open(url, "_blank", "noopener,noreferrer");
    },
    openPath: async (path: string) => {
      console.warn("openPath: 不支持 Web 版本", path);
    },
  },

  dialog: {
    showOpenDialog: async (options: any) => {
      console.log('showOpenDialog called with options:', options);
      return new Promise((resolve, reject) => {
        const input = document.createElement("input");
        input.type = "file";
        input.multiple = options.properties?.includes("multiSelections");
        input.style.display = 'none'; // 隐藏 input
        
        // 处理目录选择
        if (options.properties?.includes("openDirectory")) {
          input.webkitdirectory = true;
        }
        
        if (options.filters) {
          input.accept = options.filters
            .map((f: any) => f.extensions.map((e: string) => `.${e}`).join(","))
            .join(",");
        }
        
        console.log('Created input element:', { type: input.type, accept: input.accept, multiple: input.multiple });
        
        // 添加到 DOM 中
        document.body.appendChild(input);
        
        // 处理取消情况
        const cleanup = () => {
          console.log('Cleaning up input element');
          document.body.removeChild(input);
        };
        
        input.oncancel = () => {
          console.log('File selection cancelled');
          cleanup();
          resolve([]);
        };
        
        input.onchange = async () => {
          console.log('File input changed, files:', input.files);
          const files = Array.from(input.files || []);
          
          if (files.length === 0) {
            console.log('No files selected');
            cleanup();
            resolve([]);
            return;
          }
          
          // 只返回 File 对象，不立即上传
          // 包装成带有 name 属性的对象，方便组件显示文件名
          const fileObjects = files.map((file: File) => ({
            file, // 原始 File 对象
            name: file.name,
            size: file.size,
            type: file.type,
          }));
          
          console.log('Returning file objects:', fileObjects);
          cleanup();
          resolve(fileObjects as any);
        };
        
        console.log('Triggering input click');
        // 使用 setTimeout 确保 input 已经添加到 DOM
        setTimeout(() => {
          input.click();
        }, 0);
      });
    },
    showSaveDialog: async (options: any) => {
      return options.defaultPath || "download";
    },
    showMessageBox: async (options: any) => {
      const result = window.confirm(options.message);
      return { response: result ? 0 : 1 };
    },
    showErrorBox: async (title: string, content: string) => {
      alert(`${title}\n\n${content}`);
    },
  },

  appSettings: {
    get: async (key: string) => storage.get(`app_${key}`),
    set: async (key: string, value: any) => storage.set(`app_${key}`, value),
    getLibrary: async () => storage.get("library") || "/library",
    setLibrary: async (library: string) => storage.set("library", library),
    getSessions: async () => storage.get("sessions") || [],
    getUser: async () => storage.get("user"),
    setUser: async (user: UserType) => {
      storage.set("user", user);
      // ✅ 同时保存到 currentUser 以便添加到 API 请求 header
      storage.set("currentUser", user);
    },
    getUserDataPath: async () => "/user-data",
    getApiUrl: async () => API_BASE_URL,
    setApiUrl: async (url: string) => storage.set("api_url", url),
  },

  userSettings: {
    get: async (key: string) => storage.get(`user_${key}`),
    set: async (key: string, value: any) => storage.set(`user_${key}`, value),
  },

  fs: {
    ensureDir: async (_path: string) => {
      // 浏览器环境无需创建目录，保留兼容返回值
      return true;
    },
    readFile: async (path: string) => {
      if (isLocalFilesystemPath(path)) {
        throw new Error("Web 版不支持直接读取本地文件，请先上传到服务器。");
      }
      const resolvedUrl = normalizeWebPathToUrl(path);
      const response = await fetch(resolvedUrl);
      if (!response.ok) {
        throw new Error(`读取文件失败: ${response.status} ${response.statusText}`);
      }
      const buffer = await response.arrayBuffer();
      return Array.from(new Uint8Array(buffer));
    },
    stat: async (path: string) => {
      if (isLocalFilesystemPath(path)) {
        throw new Error("Web 版不支持读取本地文件元数据，请先上传到服务器。");
      }
      const resolvedUrl = normalizeWebPathToUrl(path);
      const response = await fetch(resolvedUrl, { method: "HEAD" });
      if (!response.ok) {
        throw new Error(`读取文件信息失败: ${response.status} ${response.statusText}`);
      }
      const contentLength = response.headers.get("content-length");
      return { size: contentLength ? Number(contentLength) : 0 };
    },
  },

  path: {
    join: async (...paths: string[]) => paths.join("/"),
  },

  db: {
    connect: async () => {
      console.log("DB: 使用 IndexedDB 或后端 API");
      // Web 版本：返回模拟的数据库状态
      return Promise.resolve({
        state: 'connected',
        path: 'web-indexeddb',
        error: undefined,
      });
    },
    disconnect: async () => {
      return Promise.resolve();
    },
    onTransaction: async (callback: any) => {
      eventBus.on("db-on-transaction", callback);
    },
    removeListeners: async () => {
      eventBus.removeAllListeners("db-on-transaction");
    },
  },

  camdict: {
    lookup: async (word: string) => {
      const res = await apiClient.get(`/api/dict/camdict/${word}`);
      return res.data;
    },
    lookupBatch: async (words: string[], signal?: AbortSignal) => {
      if (!words || words.length === 0) return {};
      try {
        const res = await apiClient.post("/api/dict/camdict/batch", { words }, {
          headers: {
            'Content-Type': 'application/json',
          },
          signal, // 支持取消请求
        });
        return res.data as Record<string, CamdictWordType | null>;
      } catch (error: any) {
        // 如果是取消请求，不记录错误
        if (error.name === 'AbortError' || error.name === 'CanceledError') {
          throw error;
        }
        console.error('[camdict.lookupBatch] Error:', error);
        // 返回空结果，避免阻塞编辑保存流程
        return {};
      }
    },
  },

  mdict: {
    remove: async (dict: any) => {
      const res = await apiClient.delete(`/api/dict/mdict/${dict.title}`);
      return res.data;
    },
    getResource: async (key: string, dict: any) => {
      const res = await apiClient.get(`/api/dict/mdict/${dict.title}/resource/${key}`);
      return res.data;
    },
    lookup: async (word: string, dict: any) => {
      const res = await apiClient.get(`/api/dict/mdict/${dict.title}/${word}`);
      return res.data;
    },
    import: async (pathes: string[]) => {
      console.warn("mdict.import: Web 版本需要通过文件上传实现");
      return { title: "", mdx: "", mdds: [], hash: "" };
    },
  },

  dict: {
    getDicts: async () => {
      const res = await apiClient.get("/api/dict/list");
      return res.data;
    },
    remove: async (dict: Dict) => {
      const res = await apiClient.delete(`/api/dict/${dict.name}`);
      return res.data;
    },
    getResource: async (key: string, dict: Dict) => {
      const res = await apiClient.get(`/api/dict/${dict.name}/resource/${key}`);
      return res.data;
    },
    lookup: async (word: string, dict: Dict) => {
      const res = await apiClient.get(`/api/dict/${dict.name}/${word}`);
      return res.data;
    },
    import: async (path: string) => {
      console.warn("dict.import: Web 版本需要通过文件上传实现");
      return null;
    },
  },

  // 所有数据库操作都通过 API 实现
  audios: {
    findAll: async (params: any) => {
      // Web 版本：从服务端获取
      const res = await apiClient.get("/api/audios", { params });
      return res.data.map((a: any) => ({ ...a, mediaType: "Audio" }));
    },
    findOne: async (params: any) => {
      // 兼容两种调用方式：
      // 1. findOne({ id }) 或 findOne({ md5 })
      // 2. findOne({ where: { id } }) 或 findOne({ where: { md5 } })
      const searchParams = params?.where || params;
      
      if (!searchParams) {
        return null;
      }
      
      if (searchParams.id) {
        const res = await apiClient.get(`/api/audios/${searchParams.id}`);
        return { ...res.data, mediaType: "Audio" };
      }
      
      if (searchParams.md5) {
        // 通过 md5 查找：需要遗历列表
        const res = await apiClient.get("/api/audios");
        const audio = res.data.find((a: any) => a.md5 === searchParams.md5);
        return audio ? { ...audio, mediaType: "Audio" } : null;
      }
      
      return null;
    },
    create: async (uri: string | any, params?: any) => {
      // 生成简单的 md5 替代（使用时间戳和随机数）
      const simpleMd5 = Date.now().toString(16) + Math.random().toString(16).slice(2);
      
      // 处理 uri 参数，可能是 URL字符串或上传结果对象
      let url, metadata, duration;
      if (uri && typeof uri === 'object' && 'url' in uri) {
        // 是上传结果对象
        url = uri.url;
        metadata = uri.metadata;
        duration = uri.duration;
      } else {
        // 是 URL字符串
        url = uri;
      }
      
      // 从 URL 中提取文件名（服务端 URL 格式： /uploads/timestamp-filename.mp3）
      const fileName = url.split("/").pop()?.split("-").slice(1).join("-") || url.split("/").pop() || "Untitled";
      
      const audioData = {
        name: fileName,
        src: `${API_BASE_URL}${url}`, // 完整的服务端 URL
        md5: simpleMd5,
        mediaType: "Audio",
        metadata,
        duration,
        ...params,
      };
      
      const res = await apiClient.post("/api/audios", audioData);
      return res.data;
    },
    update: async (id: string, params: any) => {
      const res = await apiClient.put(`/api/audios/${id}`, params);
      const audio = res.data;
      // 触发数据库更新事件，通知前端组件更新
      dispatchDbUpdate("Audio", "update", audio);
      return audio;
    },
    destroy: async (id: string) => {
      const res = await apiClient.delete(`/api/audios/${id}`);
      // 触发数据库更新事件，通知前端组件更新
      dispatchDbUpdate("Audio", "destroy", { id });
      return res.data;
    },
    upload: async (id: string) => {
      console.warn("upload: Web 版本不需要上传");
      return;
    },
    crop: async (id: string, params: any) => {
      console.warn("crop: Web 版本不支持");
      throw new Error("Crop not supported in web version");
    },
    cleanUp: async () => {
      return;
    },
    play: async (id: string) => {
      const res = await apiClient.post(`/api/audios/${id}`, { action: 'play' });
      return res.data;
    },
  },

  videos: {
    findAll: async (params: any) => {
      // Web 版本：从服务端获取
      const res = await apiClient.get("/api/videos", { params });
      return res.data.map((v: any) => ({ ...v, mediaType: "Video" }));
    },
    findOne: async (params: any) => {
      // 兼容两种调用方式：
      // 1. findOne({ id }) 或 findOne({ md5 })
      // 2. findOne({ where: { id } }) 或 findOne({ where: { md5 } })
      const searchParams = params?.where || params;
      
      if (!searchParams) {
        return null;
      }
      
      if (searchParams.id) {
        const res = await apiClient.get(`/api/videos/${searchParams.id}`);
        return { ...res.data, mediaType: "Video" };
      }
      
      if (searchParams.md5) {
        // 通过 md5 查找：需要遗历列表
        const res = await apiClient.get("/api/videos");
        const video = res.data.find((v: any) => v.md5 === searchParams.md5);
        return video ? { ...video, mediaType: "Video" } : null;
      }
      
      return null;
    },
    create: async (uri: string | any, params?: any) => {
      // 生成简单的 md5 替代（使用时间戳和随机数）
      const simpleMd5 = Date.now().toString(16) + Math.random().toString(16).slice(2);
      
      // 处理 uri 参数，可能是 URL字符串或上传结果对象
      let url, metadata, duration;
      if (uri && typeof uri === 'object' && 'url' in uri) {
        // 是上传结果对象
        url = uri.url;
        metadata = uri.metadata;
        duration = uri.duration;
      } else {
        // 是 URL字符串
        url = uri;
      }
      
      // 从 URL 中提取文件名（服务端 URL 格式： /uploads/timestamp-filename.mp4）
      const fileName = url.split("/").pop()?.split("-").slice(1).join("-") || url.split("/").pop() || "Untitled";
      
      const videoData = {
        name: fileName,
        src: `${API_BASE_URL}${url}`, // 完整的服务端 URL
        md5: simpleMd5,
        mediaType: "Video",
        metadata,
        duration,
        ...params,
      };
      
      const res = await apiClient.post("/api/videos", videoData);
      return res.data;
    },
    update: async (id: string, params: any) => {
      const res = await apiClient.put(`/api/videos/${id}`, params);
      const video = res.data;
      // 触发数据库更新事件，通知前端组件更新
      dispatchDbUpdate("Video", "update", video);
      return video;
    },
    destroy: async (id: string) => {
      const res = await apiClient.delete(`/api/videos/${id}`);
      // 触发数据库更新事件，通知前端组件更新
      dispatchDbUpdate("Video", "destroy", { id });
      return res.data;
    },
    upload: async (id: string) => {
      console.warn("upload: Web 版本不需要上传");
      return;
    },
    crop: async (id: string, params: any) => {
      console.warn("crop: Web 版本不支持");
      throw new Error("Crop not supported in web version");
    },
    cleanUp: async () => {
      return;
    },
    like: async (id: string) => {
      const res = await apiClient.post(`/api/videos/${id}`, { action: 'like' });
      return res.data;
    },
    play: async (id: string) => {
      const res = await apiClient.post(`/api/videos/${id}`, { action: 'play' });
      return res.data;
    },
  },

  conversations: {
    findAll: async (params: any) => {
      const res = await apiClient.get("/api/conversations", { params });
      return res.data;
    },
    findOne: async (params: any) => {
      const res = await apiClient.get(`/api/conversations/${params.where.id}`);
      return res.data;
    },
    create: async (params: any) => {
      const res = await apiClient.post("/api/conversations", params);
      return res.data;
    },
    update: async (id: string, params: any) => {
      const res = await apiClient.put(`/api/conversations/${id}`, params);
      return res.data;
    },
    destroy: async (id: string) => {
      const res = await apiClient.delete(`/api/conversations/${id}`);
      return res.data;
    },
    migrate: async (id: string) => {
      const res = await apiClient.post(`/api/conversations/${id}/migrate`);
      return res.data;
    },
  },

  pronunciationAssessments: {
    findAll: async (params: any) => {
      const res = await apiClient.get("/api/pronunciation-assessments", { params });
      return res.data;
    },
    findOne: async (params: any) => {
      const res = await apiClient.get(`/api/pronunciation-assessments/${params.where.id}`);
      return res.data;
    },
    create: async (params: any) => {
      const res = await apiClient.post("/api/pronunciation-assessments", params);
      return res.data;
    },
    update: async (id: string, params: any) => {
      const res = await apiClient.put(`/api/pronunciation-assessments/${id}`, params);
      return res.data;
    },
    destroy: async (id: string) => {
      const res = await apiClient.delete(`/api/pronunciation-assessments/${id}`);
      return res.data;
    },
  },

  messages: {
    findAll: async (params: any) => {
      const res = await apiClient.get("/api/messages", { params });
      return res.data;
    },
    findOne: async (where: any) => {
      const res = await apiClient.post("/api/messages/find-one", { where });
      return res.data;
    },
    createInBatch: async (messages: any[]) => {
      const res = await apiClient.post("/api/messages/batch", { messages });
      return res.data;
    },
    destroy: async (id: string) => {
      const res = await apiClient.delete(`/api/messages/${id}`);
      return res.data;
    },
    createSpeech: async (id: string, configuration?: any) => {
      const res = await apiClient.post(`/api/messages/${id}/speech`, { configuration });
      return res.data;
    },
  },

  speeches: {
    findOne: async (where: any) => {
      const res = await apiClient.post("/api/speeches/find-one", { where });
      return res.data;
    },
    create: async (params: any, blob: any) => {
      const formData = new FormData();
      formData.append("params", JSON.stringify(params));
      formData.append("audio", new Blob([blob.arrayBuffer], { type: blob.type }));
      const res = await apiClient.post("/api/speeches", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data;
    },
    delete: async (id: string) => {
      const res = await apiClient.delete(`/api/speeches/${id}`);
      return res.data;
    },
  },

  audiowaveform: {
    generate: async (file: string, options?: any) => {
      const res = await apiClient.post("/api/audiowaveform/generate", { file, options });
      return res.data;
    },
    frequencies: async (file: string) => {
      const res = await apiClient.post("/api/audiowaveform/frequencies", { file });
      return res.data;
    },
  },

  echogarden: {
    getPackagesDir: async () => "/echogarden-packages",
    recognize: async (input: string, options: any) => {
      const res = await apiClient.post("/api/echogarden/recognize", { input, options });
      return res.data;
    },
    align: async (input: Uint8Array | string, transcript: string, options: any) => {
      const formData = new FormData();
      
      // 处理音频数据
      if (input instanceof Uint8Array) {
        formData.append('audio', new Blob([input]), 'audio.mp3');
      } else {
        // 如果是 URL，需要 fetch 获取 blob
        const blob = await (await fetch(input)).blob();
        formData.append('audio', blob, 'audio.mp3');
      }
      
      formData.append('transcript', transcript);
      formData.append('options', JSON.stringify(options));
      
      // 使用原生 fetch 替代 apiClient
      const url = `${API_BASE_URL}/api/echogarden/align`;
      console.log('Sending align request to:', url);
      
      // 添加超时控制
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 1000 * 60 * 7); // 7分钟超时
      
      try {
        const response = await fetch(url, {
          method: 'POST',
          body: formData,
          signal: controller.signal,
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Align request failed:', response.status, errorData);
          throw new Error(errorData.message || `Alignment failed: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Align response data:', data);
        return data;
      } catch (error) {
        if (error.name === 'AbortError') {
          throw new Error('Alignment request timed out');
        }
        throw error;
      } finally {
        clearTimeout(timeoutId);
      }
    },
    alignSegments: async (input: Uint8Array | string, timeline: any, options: any) => {
      // 使用新的 /api/transcriptions/align-segments 接口
      const formData = new FormData();
      
      // 处理音频数据
      if (input instanceof Uint8Array) {
        formData.append('audio', new Blob([input]));
      } else {
        // 如果是 URL，需要 fetch 获取 blob
        const blob = await (await fetch(input)).blob();
        formData.append('audio', blob);
      }
      
      formData.append('timeline', JSON.stringify(timeline));
      formData.append('options', JSON.stringify(options));
      
      const res = await apiClient.post("/api/transcriptions/align-segments", formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 1000 * 60 * 3, // 3分钟超时，用于处理长视频
      });
      return res.data;
    },
    wordToSentenceTimeline: async (wordTimeline: any, transcript: string, language: string) => {
      const res = await apiClient.post("/api/transcriptions/word-to-sentence-timeline", {
        wordTimeline,
        transcript,
        language,
      });
      return res.data;
    },
    transcode: async (input: string) => {
      const res = await apiClient.post("/api/echogarden/transcode", { input });
      return res.data;
    },
    check: async (options: any) => {
      const res = await apiClient.post("/api/echogarden/check", { options });
      return res.data;
    },
    checkAlign: async (options: any) => {
      const res = await apiClient.post("/api/echogarden/check-align", { options });
      return res.data;
    },
  },

  ffmpeg: {
    check: async () => {
      const res = await apiClient.get("/api/ffmpeg/check");
      return res.data;
    },
    transcode: async (input: string, output: string, options: string[]) => {
      const res = await apiClient.post("/api/ffmpeg/transcode", { input, output, options });
      return res.data;
    },
  },

  decompress: {
    onComplete: () => {},
    onUpdate: () => {},
    dashboard: async () => [],
    removeAllListeners: () => {},
  },

  download: {
    onState: (callback: any) => {
      eventBus.on("download-on-state", callback);
    },
    start: async (url: string, savePath?: string) => {
      const res = await apiClient.post("/api/download/start", { url, savePath });
      return res.data;
    },
    printAsPdf: async (content: string, savePath: string) => {
      console.warn("printAsPdf: Web 版本使用浏览器打印功能");
      window.print();
    },
    cancel: async (filename: string) => {
      const res = await apiClient.post("/api/download/cancel", { filename });
      return res.data;
    },
    pause: async (filename: string) => {
      const res = await apiClient.post("/api/download/pause", { filename });
      return res.data;
    },
    remove: async (filename: string) => {
      const res = await apiClient.post("/api/download/remove", { filename });
      return res.data;
    },
    resume: async (filename: string) => {
      const res = await apiClient.post("/api/download/resume", { filename });
      return res.data;
    },
    cancelAll: async () => {
      const res = await apiClient.post("/api/download/cancel-all");
      return res.data;
    },
    dashboard: async () => {
      const res = await apiClient.get("/api/download/dashboard");
      return res.data;
    },
    removeAllListeners: () => {
      eventBus.removeAllListeners("download-on-state");
    },
  },

  cacheObjects: {
    get: async (key: string) => {
      const res = await apiClient.get(`/api/cache/${key}`);
      return res.data;
    },
    set: async (key: string, value: any, ttl?: number) => {
      const res = await apiClient.post("/api/cache", { key, value, ttl });
      return res.data;
    },
    delete: async (key: string) => {
      const res = await apiClient.delete(`/api/cache/${key}`);
      return res.data;
    },
    clear: async () => {
      const res = await apiClient.delete("/api/cache");
      return res.data;
    },
    writeFile: async (filename: string, data: any) => {
      const formData = new FormData();
      formData.append("file", new Blob([data]));
      formData.append("filename", filename);
      const res = await apiClient.post("/api/cache/file", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data;
    },
  },

  transcriptions: {
    findOrCreate: async (params: any) => {
      const res = await apiClient.post("/api/transcriptions/find-or-create", params);
      return res.data;
    },
    update: async (id: string, params: any) => {
      try {
        const res = await apiClient.put(`/api/transcriptions/${id}`, params, {
          timeout: 60000, // 增加超时时间到60秒，因为保存可能需要较长时间
        });
        return res.data;
      } catch (error) {
        console.error('[transcriptions.update] Error:', error);
        throw error;
      }
    },
  },

  waveforms: {
    find: async (id: string) => {
      try {
        const res = await apiClient.get(`/api/waveforms/${id}`);
        return res.data;
      } catch (error) {
        console.error('[Waveforms] Error fetching from server:', error);
        return null;
      }
    },
    save: async (id: string, data: any) => {
      console.log('[Waveforms] Saving waveform data for', id);
      
      // 异步保存到服务器（不阻塞）
      apiClient.post(`/api/waveforms/${id}`, { data }).catch((error) => {
        console.error('[Waveforms] Error saving to server:', error);
      });
      
      return data;
    },
  },

  segments: {
    findAll: async (params: any) => {
      const res = await apiClient.get("/api/segments", { params });
      return res.data;
    },
    find: async (id: string) => {
      const res = await apiClient.get(`/api/segments/${id}`);
      return res.data;
    },
    create: async (params: any) => {
      const res = await apiClient.post("/api/segments", params);
      return res.data;
    },
    sync: async (id: string) => {
      const res = await apiClient.post(`/api/segments/${id}/sync`);
      return res.data;
    },
  },

  recordings: {
    findAll: async (params: any): Promise<any[]> => {
      console.warn("recordings.findAll: Web 版本暂不支持录音功能");
      return [];
    },
    find: async (id: string): Promise<any> => {
      console.warn("recordings.find: Web 版本暂不支持录音功能");
      return null;
    },
    groupBySegment: async (targetId: string, targetType: string): Promise<any[]> => {
      return [];
    },
    create: async (params: any): Promise<any> => {
      console.warn("recordings.create: Web 版本暂不支持录音功能");
      throw new Error("Web 版本暂不支持录音功能");
    },
    update: async (id: string, params: any): Promise<any> => {
      console.warn("recordings.update: Web 版本暂不支持录音功能");
      throw new Error("Web 版本暂不支持录音功能");
    },
    destroy: async (id: string): Promise<any> => {
      console.warn("recordings.destroy: Web 版本暂不支持录音功能");
      throw new Error("Web 版本暂不支持录音功能");
    },
    upload: async (id: string): Promise<any> => {
      console.warn("recordings.upload: Web 版本暂不支持录音功能");
      throw new Error("Web 版本暂不支持录音功能");
    },
    sync: async (id: string): Promise<any> => {
      console.warn("recordings.sync: Web 版本暂不支持录音功能");
      throw new Error("Web 版本暂不支持录音功能");
    },
  },

  notes: {
    groupByTarget: async (params?: any) => {
      const res = await apiClient.get("/api/notes/group-by-target", { params });
      return res.data;
    },
    groupBySegment: async (targetId: string, targetType: string) => {
      const res = await apiClient.get(`/api/notes/group-by-segment/${targetType}/${targetId}`);
      return res.data;
    },
    findAll: async (params: any) => {
      const res = await apiClient.get("/api/notes", { params });
      return res.data;
    },
    find: async (id: string) => {
      const res = await apiClient.get(`/api/notes/${id}`);
      return res.data;
    },
    update: async (id: string, params: any) => {
      const res = await apiClient.put(`/api/notes/${id}`, params);
      return res.data;
    },
    delete: async (id: string) => {
      const res = await apiClient.delete(`/api/notes/${id}`);
      return res.data;
    },
    create: async (params: any) => {
      const res = await apiClient.post("/api/notes", params);
      return res.data;
    },
    sync: async (id: string) => {
      const res = await apiClient.post(`/api/notes/${id}/sync`);
      return res.data;
    },
  },

  chats: {
    findAll: async (params: any) => {
      const res = await apiClient.get("/api/chats", { params });
      return res.data;
    },
    findOne: async (params: any) => {
      const res = await apiClient.get(`/api/chats/${params.where.id}`);
      return res.data;
    },
    create: async (params: any) => {
      const res = await apiClient.post("/api/chats", params);
      return res.data;
    },
    update: async (id: string, params: any) => {
      const res = await apiClient.put(`/api/chats/${id}`, params);
      return res.data;
    },
    destroy: async (id: string) => {
      const res = await apiClient.delete(`/api/chats/${id}`);
      return res.data;
    },
  },

  chatAgents: {
    findAll: async (params: any) => {
      const res = await apiClient.get("/api/chat-agents", { params });
      return res.data;
    },
    findOne: async (params: any) => {
      const res = await apiClient.get(`/api/chat-agents/${params.where.id}`);
      return res.data;
    },
    create: async (params: any) => {
      const res = await apiClient.post("/api/chat-agents", params);
      return res.data;
    },
    update: async (id: string, params: any) => {
      const res = await apiClient.put(`/api/chat-agents/${id}`, params);
      return res.data;
    },
    destroy: async (id: string) => {
      const res = await apiClient.delete(`/api/chat-agents/${id}`);
      return res.data;
    },
  },

  chatMembers: {
    findAll: async (params: any) => {
      const res = await apiClient.get("/api/chat-members", { params });
      return res.data;
    },
    findOne: async (params: any) => {
      const res = await apiClient.get(`/api/chat-members/${params.where.id}`);
      return res.data;
    },
    create: async (params: any) => {
      const res = await apiClient.post("/api/chat-members", params);
      return res.data;
    },
    update: async (id: string, params: any) => {
      const res = await apiClient.put(`/api/chat-members/${id}`, params);
      return res.data;
    },
    destroy: async (id: string) => {
      const res = await apiClient.delete(`/api/chat-members/${id}`);
      return res.data;
    },
  },

  chatMessages: {
    findAll: async (params: any) => {
      const res = await apiClient.get("/api/chat-messages", { params });
      return res.data;
    },
    findOne: async (params: any) => {
      const res = await apiClient.get(`/api/chat-messages/${params.where.id}`);
      return res.data;
    },
    create: async (params: any) => {
      const res = await apiClient.post("/api/chat-messages", params);
      return res.data;
    },
    update: async (id: string, params: any) => {
      const res = await apiClient.put(`/api/chat-messages/${id}`, params);
      return res.data;
    },
    destroy: async (id: string) => {
      const res = await apiClient.delete(`/api/chat-messages/${id}`);
      return res.data;
    },
  },

  documents: {
    findAll: async (params: any) => {
      const res = await apiClient.get("/api/documents", { params });
      return res.data;
    },
    findOne: async (params: any) => {
      const res = await apiClient.get(`/api/documents/${params.where.id}`);
      return res.data;
    },
    create: async (params: any) => {
      const res = await apiClient.post("/api/documents", params);
      return res.data;
    },
    update: async (id: string, params: any) => {
      const res = await apiClient.put(`/api/documents/${id}`, params);
      return res.data;
    },
    destroy: async (id: string) => {
      const res = await apiClient.delete(`/api/documents/${id}`);
      return res.data;
    },
    upload: async (id: string) => {
      const res = await apiClient.post(`/api/documents/${id}/upload`);
      return res.data;
    },
    cleanUp: async () => {
      const res = await apiClient.post("/api/documents/cleanup");
      return res.data;
    },
  },

  // 用户角色查询
  me: {
    getRole: async () => {
      const res = await apiClient.get("/api/me/role");
      return res.data;
    },
  },

  // 课程中心
  courseCenter: {
    audios: {
      findAll: async (params: any) => {
        const res = await apiClient.get("/api/course-center/audios", { params });
        return res.data.map((a: any) => ({ ...a, mediaType: "CourseAudio" }));
      },
      findOne: async (id: string) => {
        const res = await apiClient.get(`/api/course-center/audios/${id}`);
        return { ...res.data, mediaType: "CourseAudio" };
      },
      create: async (uri: string | any, params?: any) => {
        const simpleMd5 = Date.now().toString(16) + Math.random().toString(16).slice(2);
        
        let url, metadata, duration;
        if (uri && typeof uri === 'object' && 'url' in uri) {
          url = uri.url;
          metadata = uri.metadata;
          duration = uri.duration;
        } else {
          url = uri;
        }
        
        const fileName = url.split("/").pop()?.split("-").slice(1).join("-") || url.split("/").pop() || "Untitled";
        
        const audioData = {
          name: params?.name || fileName,
          src: `${API_BASE_URL}${url}`,
          md5: simpleMd5,
          metadata,
          duration,
          language: params?.language || 'en-US',
          description: params?.description || '',
          ...params,
        };
        
        const res = await apiClient.post("/api/course-center/audios", audioData);
        return { ...res.data, mediaType: "CourseAudio" };
      },
      update: async (id: string, params: any) => {
        const res = await apiClient.put(`/api/course-center/audios/${id}`, params);
        return { ...res.data, mediaType: "CourseAudio" };
      },
      destroy: async (id: string) => {
        const res = await apiClient.delete(`/api/course-center/audios/${id}`);
        dispatchDbUpdate("CourseAudio", "destroy", { id });
        return res.data;
      },
      destroyBatch: async (ids: string[]) => {
        const res = await apiClient.delete("/api/course-center/audios/batch", {
          data: { ids },
        });
        ids.forEach((id) => dispatchDbUpdate("CourseAudio", "destroy", { id }));
        return res.data;
      },
      learn: async (id: string) => {
        const res = await apiClient.post(`/api/course-center/audios/${id}/learn`);
        return res.data;
      },
      play: async (id: string) => {
        const res = await apiClient.post(`/api/course-center/audios/${id}`, {
          action: "play",
        });
        return res.data;
      },
    },
    videos: {
      findAll: async (params: any) => {
        const res = await apiClient.get("/api/course-center/videos", { params });
        return res.data.map((v: any) => ({ ...v, mediaType: "CourseVideo" }));
      },
      findOne: async (id: string) => {
        const res = await apiClient.get(`/api/course-center/videos/${id}`);
        return { ...res.data, mediaType: "CourseVideo" };
      },
      create: async (uri: string | any, params?: any) => {
        const simpleMd5 = Date.now().toString(16) + Math.random().toString(16).slice(2);
        
        let url, metadata, duration;
        if (uri && typeof uri === 'object' && 'url' in uri) {
          url = uri.url;
          metadata = uri.metadata;
          duration = uri.duration;
        } else {
          url = uri;
        }
        
        const fileName = url.split("/").pop()?.split("-").slice(1).join("-") || url.split("/").pop() || "Untitled";
        
        const videoData = {
          name: params?.name || fileName,
          src: `${API_BASE_URL}${url}`,
          md5: simpleMd5,
          metadata,
          duration,
          language: params?.language || 'en-US',
          description: params?.description || '',
          ...params,
        };
        
        console.log('[web-adapter] Creating course video with name:', videoData.name);
        console.log('[web-adapter] Name char codes:', [...(videoData.name || '')].map(c => c.codePointAt(0)?.toString(16)));
        
        const res = await apiClient.post("/api/course-center/videos", videoData);
        return { ...res.data, mediaType: "CourseVideo" };
      },
      update: async (id: string, params: any) => {
        const res = await apiClient.put(`/api/course-center/videos/${id}`, params);
        return { ...res.data, mediaType: "CourseVideo" };
      },
      destroy: async (id: string) => {
        const res = await apiClient.delete(`/api/course-center/videos/${id}`);
        dispatchDbUpdate("CourseVideo", "destroy", { id });
        return res.data;
      },
      destroyBatch: async (ids: string[]) => {
        const res = await apiClient.delete("/api/course-center/videos/batch", {
          data: { ids },
        });
        ids.forEach((id) => dispatchDbUpdate("CourseVideo", "destroy", { id }));
        return res.data;
      },
      learn: async (id: string) => {
        const res = await apiClient.post(`/api/course-center/videos/${id}/learn`);
        return res.data;
      },
      play: async (id: string) => {
        const res = await apiClient.post(`/api/course-center/videos/${id}`, {
          action: "play",
        });
        return res.data;
      },
      like: async (id: string) => {
        const res = await apiClient.post(`/api/course-center/videos/${id}`, {
          action: "like",
        });
        return res.data;
      },
    },
  },

  // 训练时长和积分系统
  training: {
    record: async (params: {
      userId: string;
      resourceType: string;
      resourceId: string;
      duration: number;
      sessionId: string;
    }) => {
      console.log('[WebAdapter] Training record called:', params);
      try {
        const res = await apiClient.post("/api/training/record", params);
        console.log('[WebAdapter] Training record success:', res.data);
        return res.data;
      } catch (err: any) {
        console.error('[WebAdapter] Training record error:', err.message, err.response?.data);
        throw err;
      }
    },
    recordBatch: async (params: {
      userId: string;
      resourceType: string;
      resourceId: string;
      sessionId: string;
      records: Array<{ duration: number; timestamp: number }>;
      totalDuration: number;
    }) => {
      console.log('[WebAdapter] Training recordBatch called:', params);
      try {
        const res = await apiClient.post("/api/training/record/batch", params);
        console.log('[WebAdapter] Training recordBatch success:', res.data);
        return res.data;
      } catch (err: any) {
        console.error('[WebAdapter] Training recordBatch error:', err.message, err.response?.data);
        throw err;
      }
    },
    getStats: async () => {
      console.log('[WebAdapter] Training getStats called');
      try {
        const res = await apiClient.get("/api/training/stats");
        console.log('[WebAdapter] Training getStats success:', res.data);
        return res.data;
      } catch (err: any) {
        console.error('[WebAdapter] Training getStats error:', err.message, err.response?.data);
        throw err;
      }
    },
    getPointsBalance: async () => {
      console.log('[WebAdapter] Training getPointsBalance called');
      try {
        const res = await apiClient.get("/api/points/balance");
        console.log('[WebAdapter] Training getPointsBalance success:', res.data);
        return res.data;
      } catch (err: any) {
        console.error('[WebAdapter] Training getPointsBalance error:', err.message, err.response?.data);
        throw err;
      }
    },
    getPointsHistory: async (params?: { page?: number; limit?: number; type?: string }) => {
      const res = await apiClient.get("/api/points/history", { params });
      return res.data;
    },
    getResource: async (params: { resourceType: string; resourceId: string }) => {
      console.log('[WebAdapter] Training getResource called:', params);
      try {
        const res = await apiClient.get("/api/training/resource", { params });
        console.log('[WebAdapter] Training getResource success:', res.data);
        return res.data;
      } catch (err: any) {
        console.error('[WebAdapter] Training getResource error:', err.message, err.response?.data);
        throw err;
      }
    },
    getResources: async (params: { resourceType: string }) => {
      console.log('[WebAdapter] Training getResources called:', params);
      try {
        const res = await apiClient.get("/api/training/resources", { params });
        console.log('[WebAdapter] Training getResources success:', res.data);
        return res.data;
      } catch (err: any) {
        console.error('[WebAdapter] Training getResources error:', err.message, err.response?.data);
        throw err;
      }
    },
    consumePoints: async (params: {
      resourceType: string;
      resourceId: string;
      pointsToConsume: number;
    }) => {
      const res = await apiClient.post("/api/points/consume", params);
      return res.data;
    },
  },
});
